import React, { useState, useEffect } from "react";
import PaymentMethod from "./PaymentMethod";
import ChooseCardModal from "./ChooseCardModal";
import AddCardSavings from "./AddCardSavings";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import TextError from "../SelectValue/TextError";
import AddIcon from "@mui/icons-material/Add";
import { useNavigate } from "react-router-dom";
import getServices from "../../Services/get-service";

const DEFAULT_CARDS = [
  {
    id: "card_1",
    brand: "mastercard",
    last4: "2898",
    expiry: "08/26",
    bankName: "Access Bank",
    cardholder: "Default Card",
    isDefault: true,
  },
  {
    id: "card_2",
    brand: "visa",
    last4: "4312",
    expiry: "11/25",
    bankName: "GTBank",
    cardholder: "Personal Card",
    isDefault: false,
  },
  {
    id: "card_3",
    brand: "mastercard",
    last4: "8701",
    expiry: "04/27",
    bankName: "Zenith Bank",
    cardholder: "Savings Card",
    isDefault: false,
  },
];

function CreateCard() {
  const navigate = useNavigate();

  // Payment method selection ('WALLET' or 'CARD')
  const [paymentMethod, setPaymentMethod] = useState(() => {
    return sessionStorage.getItem("paymentMethod") || "WALLET";
  });

  // User cards list
  const [cards, setCards] = useState(() => {
    try {
      const stored = localStorage.getItem("zouse_saved_cards");
      return stored ? JSON.parse(stored) : DEFAULT_CARDS;
    } catch (e) {
      return DEFAULT_CARDS;
    }
  });

  // Selected card
  const [selectedCard, setSelectedCard] = useState(() => {
    try {
      const stored = sessionStorage.getItem("selectedCard");
      return stored ? JSON.parse(stored) : DEFAULT_CARDS[0];
    } catch (e) {
      return DEFAULT_CARDS[0];
    }
  });

  // Wallet balance
  const [walletBalance, setWalletBalance] = useState(50000.89);

  // Modal open states
  const [openChooseCard, setOpenChooseCard] = useState(false);
  const [openAddCard, setOpenAddCard] = useState(false);

  useEffect(() => {
    // Fetch live wallet balance
    getServices
      .getWalletBalance()
      .then((res) => {
        if (res.data?.statusCode === 200 && res.data.data?.length > 0) {
          setWalletBalance(res.data.data[0].availableBalance);
        }
      })
      .catch(() => {});
  }, []);

  const handleSelectMethod = (method) => {
    setPaymentMethod(method);
    sessionStorage.setItem("paymentMethod", method);
  };

  const handleSelectCard = (card) => {
    setSelectedCard(card);
    setPaymentMethod("CARD");
    sessionStorage.setItem("selectedCard", JSON.stringify(card));
    sessionStorage.setItem("paymentMethod", "CARD");
    setOpenChooseCard(false);
  };

  const handleCardAdded = (newCard) => {
    const updated = [newCard, ...cards];
    setCards(updated);
    try {
      localStorage.setItem("zouse_saved_cards", JSON.stringify(updated));
    } catch (e) {}

    setSelectedCard(newCard);
    setPaymentMethod("CARD");
    sessionStorage.setItem("selectedCard", JSON.stringify(newCard));
    sessionStorage.setItem("paymentMethod", "CARD");
  };

  const initialValues = {
    amount: sessionStorage.getItem("savingsAmount") || "",
  };

  const validationSchema = Yup.object({
    amount: Yup.string()
      .required("Amount is required")
      .test("is-greater-than-0", "Amount must be greater than 0", (value) => {
        if (!value) return false;
        const num = Number(value.replace(/,/g, ""));
        return !isNaN(num) && num > 0;
      }),
  });

  const onSubmit = (values) => {
    const { amount } = values;
    sessionStorage.setItem("savingsAmount", amount);
    sessionStorage.setItem("paymentMethod", paymentMethod);
    if (selectedCard) {
      sessionStorage.setItem("selectedCard", JSON.stringify(selectedCard));
    }
    navigate("/save/plan/paymentsummary");
  };

  return (
    <div>
      <div>
        <h1 className="text-back-color2 text-[18px] mb-4 font-[600]">
          How much would you like to get started with?
        </h1>

        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={onSubmit}
          enableReinitialize
        >
          {({ errors, touched }) => (
            <Form>
              {/* Amount Input */}
              <div className="mb-5">
                <p className="mb-2 text-[13px] font-[500] text-[#1E222B]">
                  Enter amount(NGN)
                </p>
                <Field
                  name="amount"
                  type="text"
                  placeholder="000000"
                  className={`w-full bg-[#FAFBFC] border ${
                    errors.amount && touched.amount
                      ? "border-red-500"
                      : "border-gray-200/90"
                  } rounded-xl px-4 py-3.5 text-[15px] font-[500] text-[#1E222B] placeholder:text-gray-400 focus:outline-none focus:border-[#00BC67] transition`}
                />
                <ErrorMessage name="amount" component={TextError} />
              </div>

              {/* Payment Method Section */}
              <div className="my-4">
                <p className="font-[600] mb-3 text-back-color2 text-[15px]">
                  Select a payment method
                </p>

                <PaymentMethod
                  value={paymentMethod}
                  onChange={handleSelectMethod}
                  selectedCard={selectedCard}
                  walletBalance={walletBalance}
                />
              </div>

              {/* Action Buttons for Cards */}
              <div className="mt-3 flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => setOpenChooseCard(true)}
                  className="bg-[#18191B] hover:bg-black text-white font-[600] text-[13px] sm:text-[14px] px-4 py-2.5 rounded-xl cursor-pointer transition shadow-2xs"
                >
                  Choose other card
                </button>

                <button
                  type="button"
                  onClick={() => setOpenAddCard(true)}
                  className="bg-[#FAFBFC] hover:bg-gray-100 border border-gray-200/80 text-[#00BC67] font-[600] text-[13px] sm:text-[14px] px-4 py-2.5 rounded-xl cursor-pointer transition flex items-center gap-1 shadow-2xs"
                >
                  <span>New card</span>
                  <AddIcon sx={{ fontSize: 17, color: "#00BC67" }} />
                </button>
              </div>

              {/* Next CTA */}
              <div className="mt-8 mb-2">
                <button
                  type="submit"
                  className="w-full py-3.5 rounded-xl bg-[#00BC67] hover:bg-[#00a85c] text-white font-[700] text-[15px] cursor-pointer transition shadow-xs text-center"
                >
                  Next
                </button>
              </div>
            </Form>
          )}
        </Formik>
      </div>

      {/* Modals */}
      <ChooseCardModal
        open={openChooseCard}
        handleClose={() => setOpenChooseCard(false)}
        cards={cards}
        selectedCard={selectedCard}
        onSelectCard={handleSelectCard}
        onOpenAddCard={() => {
          setOpenChooseCard(false);
          setOpenAddCard(true);
        }}
      />

      <AddCardSavings
        open={openAddCard}
        handleClose={() => setOpenAddCard(false)}
        onCardAdded={handleCardAdded}
      />
    </div>
  );
}

export default CreateCard;